import React, { useEffect, useRef, useState, useCallback } from "react";
import configProvider from "config/configProvider";
import {
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Skeleton,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import orgDataService from "services/orgDataService";
import { Hub } from "common/models";
import vehiclesService from "services/vehiclesService";
import "mapbox-gl/dist/mapbox-gl.css";
import vanIcon from "assets/images/airport_shuttle_24dp.svg";
import hubIcon from "assets/images/red-map-pin-60.png";
import { Map, Source, Layer, MapRef, Popup } from "react-map-gl";
import { clusterLayer, unclusteredPointLayer, hubLayer } from "./layers";
import type { GeoJSONSource, Projection } from "react-map-gl";
import type { FeatureCollection } from "geojson";
import MDBox from "components/MDBox";
import { bbox, lineString } from "@turf/turf";
import { Link } from "react-router-dom";
import moment from "moment";
import VehicleUnitNumberSearch from "common/components/search/VehicleUnitNumber";
import { toast } from "react-toastify";
import { webglUnavailableMessage } from "../webglSupport";
import { mapUnavailableMessage, useMapWebglRecovery } from "map/useMapWebglRecovery";

const cameraDefault = {
  longitude: -100,
  latitude: 40,
  zoom: 3.5,
};
const CurrentLocations = () => {
  const mapRef = useRef<MapRef>(null);
  // Rebuilds the map if its WebGL context dies; see the hook for why.
  const { mapKey, reuseMaps, mapUnavailable, retry } = useMapWebglRecovery(mapRef);
  const [mapCamera, setMapCamera] = useState(cameraDefault);
  const [lng, setLng] = useState(-70.9);
  const [lat, setLat] = useState(42.35);
  const [hubId, setHubId] = useState("");
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [hubSource, setHubSource] = useState<FeatureCollection>({ type: "FeatureCollection", features: [] });
  const [locations, setLocations] = useState<FeatureCollection>({ type: "FeatureCollection", features: [] });
  const [hoverInfo, setHoverInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unitNumber, setUnitNumber] = useState(undefined);
  const [allVehicleData, setAllVehicleData] = useState(undefined);
  const [layerVisibility, setLayerVisibility] = useState({ vehicles: true, hubs: true, satellite: false });
  const [mapStyle, setMapStyle] = useState("mapbox://styles/mapbox/streets-v11");
  const [projection, setProjection] = useState<Projection>({name: 'mercator'});
  const [mapError, setMapError] = useState(false);

  useEffect(() => {
    orgDataService
      .getAllHubs()
      .then((data) => {
        setHubs(data);
        let fc: FeatureCollection = {
          type: "FeatureCollection",
          features: data.map((r) => ({
            type: "Feature",
            properties: { ...r },
            geometry: { type: "Point", coordinates: [r.longitude, r.latitude] },
          })),
        };
        setHubSource(fc);
        getAllVehicles();
      })
      .finally(() => setLoading(false));
    return () => {
      mapRef.current = null;
    };
  }, []);

  const handleLayerToggle = (e) => {
    let map = mapRef.current.getMap();

    if (e.target.id === "satellite") {
      if (mapRef.current.getStyle().name === "Mapbox Satellite Streets") {
        setMapStyle("mapbox://styles/mapbox/streets-v11");
      } else {
        setMapStyle("mapbox://styles/mapbox/satellite-streets-v12");
      }
    } else if (e.target.id === "vehicles") {
      map.setLayoutProperty(`clusters`, "visibility", e.target.checked ? "visible" : "none");
      map.setLayoutProperty(`unclustered-point`, "visibility", e.target.checked ? "visible" : "none");
      map.setLayoutProperty(`current-location-point`, "visibility", e.target.checked ? "visible" : "none");
    } else {
      map.setLayoutProperty(`${e.target.id}-layer`, "visibility", e.target.checked ? "visible" : "none");
    }

    layerVisibility[e.target.id] = e.target.checked;
    setLayerVisibility({ ...layerVisibility });
  };

  const getAllVehicles = () => {
    var found = hubs.find((x) => x.id === +hubId);
    if (found) {
      setMapCamera({ zoom: 9, longitude: found.longitude, latitude: found.latitude });
    } else {
      setMapCamera({ zoom: 9, longitude: lng, latitude: lat });
    }
    vehiclesService.getVehicleLocationByHub(+hubId).then((results) => {
      setAllVehicleData(results);
      showAllVehiclesOnMap(results);
    });
  };

  const showAllVehiclesOnMap = (data) => {
    let fc: FeatureCollection = {
      type: "FeatureCollection",
      features: data.map((r) => ({
        type: "Feature",
        properties: { ...r },
        geometry: { type: "Point", coordinates: [r.telemetry[0].longitude, r.telemetry[0].latitude] },
      })),
    };
    setLocations(fc);
    setHubId(" ");
    setMapCamera(cameraDefault);
  };

  useEffect(() => {
    if (!unitNumber && !allVehicleData) return;

    if (!unitNumber) {
      showAllVehiclesOnMap(allVehicleData);
    } else {
      let vehicleData = allVehicleData.filter((v) => v.unitNumber == unitNumber);
      if (vehicleData.length === 0) {
        toast.error(`No Location Data found for: ${unitNumber}`);
        showAllVehiclesOnMap(allVehicleData);

        return;
      }
      let fc: FeatureCollection = {
        type: "FeatureCollection",
        features: vehicleData.map((r) => ({
          type: "Feature",
          properties: { ...r },
          geometry: { type: "Point", coordinates: [r.telemetry[0].longitude, r.telemetry[0].latitude] },
        })),
      };
      setLocations(fc);
      setMapCamera({
        zoom: 9,
        longitude: vehicleData[0].telemetry[0].longitude,
        latitude: vehicleData[0].telemetry[0].latitude,
      });
    }
    setHubId(" ");
  }, [unitNumber]);

  useEffect(() => {
    if (!hubId || !mapRef?.current) return;
    var found = hubs.find((x) => x.id === +hubId);
    if (found) {
      setMapCamera({ zoom: 9, longitude: found.longitude, latitude: found.latitude });
    } else {
      if (locations.features.length > 2) {
        const points = locations.features.map((f) => f.geometry["coordinates"]);
        const line = lineString(points);
        const [minLng, minLat, maxLng, maxLat] = bbox(line);
        mapRef.current.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          { padding: 40, duration: 1000 }
        );
      }
    }
  }, [hubId]);

  const onClick = (event) => {
    const feature = event.features[0];

    if (!feature) return;

    const clusterId = feature.properties.cluster_id;
    const mapboxSource = mapRef.current.getSource("vehicles") as GeoJSONSource;

    if (clusterId) {
      mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) {
          return;
        }

        mapRef.current.easeTo({
          center: feature.geometry.coordinates,
          zoom,
          duration: 500,
        });
      });
    } else {
      let zoomCamera = mapCamera.zoom < 17 ? 17 : mapCamera.zoom;
      if (mapCamera.zoom < 20)
        mapRef.current.easeTo({
          center: feature.geometry.coordinates,
          zoom: zoomCamera,
          duration: 700,
        });
    }
  };

  const onLoad = (e) => {
    if (!mapRef.current) return;

    loadImagesAndSources();
  };

  const loadImagesAndSources = ()=>{
    if (!mapRef.current) return;

    const pinImage = new Image();
    pinImage.onload = () => {
      if (!mapRef.current.hasImage("pin")) {
        mapRef.current.addImage("pin", pinImage, { sdf: true });
      }
    };
    pinImage.src = vanIcon; // pin is your svg import
    pinImage.height = 25;
    pinImage.width = 25;
    pinImage.className = "van-pin";

    const hubImage = new Image();
    hubImage.onload = () => {
      if (!mapRef.current.hasImage("hub")) {
        mapRef.current.addImage("hub", hubImage);
       }
    };
    hubImage.src = hubIcon; // pin is your svg import
    hubImage.height = 25;
    hubImage.width = 25;
    hubImage.className = "hub-pin";
  };

  const onHover = useCallback((event: any) => {
    const {
      features,
      point: { x, y },
      lngLat,
    } = event;
    if (features && (features[0]?.layer?.id == "unclustered-point" || features[0]?.layer?.id == "hubs-layer")) {
      const hoveredFeature = features && features[0];

      // prettier-ignore
      setHoverInfo(hoveredFeature && {feature: hoveredFeature, x: lngLat?.lat, y:lngLat?.lng});
    } else return;
  }, []);

  const skeleton = () => {
    return (
      <>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4} lg={3}>
              <Skeleton variant="rectangular" height={50} />
            </Grid>
          </Grid>
        </MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12} xl={11}>
            {" "}
            <Skeleton variant="rectangular" height={"74vh"} />
          </Grid>
        </Grid>
      </>
    );
  };
  const parseProperty = (p) => {
    try {
      var propertyArray = JSON.parse(p);
      if (Array.isArray(propertyArray) && propertyArray.length > 0) {
        return propertyArray[0];
      }
    } catch {}
    return undefined;
  };
  const parseDate = (dateToParse) => {
    let date = parseProperty(dateToParse);
    return date?.lastUpdateDT ? moment.utc(date.lastUpdateDT).local().format("MM/DD/YYYY HH:mm") : null;
  };
  const parseDatesForLink = (dateToParse) => {
    let startDate = parseDate(dateToParse);
    return startDate
      ? `&startDate=${moment(startDate).subtract(1, "days").toISOString()}&endDate=${moment(startDate).toISOString()}`
      : `&startDate=${moment().subtract(1, "days").toISOString()}&endDate=${moment().toISOString()}`;
  };
  const handleStyleChange = ()=> {
    loadImagesAndSources();
  }
  const mapView = () => {
    return (
      <>
        <div>
          <MDBox>
            <FormControl sx={{ mt: 1, mb: 2, ml: 1, minWidth: 150 }}>
              <InputLabel id="hubLabelId">Hub</InputLabel>

              <Select
                id="hubId"
                name="hubId"
                labelId="hubLabelId"
                label="hub"
                value={hubId || ""}
                onChange={(e) => setHubId(e.target.value)}
              >
                <MenuItem key={0} selected value=" ">
                  {"All"}
                </MenuItem>
                {hubs &&
                  hubs.map((h, index) => {
                    return (
                      <MenuItem key={index} value={h.id}>
                        {h.name}
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
            <VehicleUnitNumberSearch
              marginTop={1}
              marginLeft={1}
              unitNumber={unitNumber ? { name: `${unitNumber}`, id: unitNumber } : null}
              onChange={(value) => setUnitNumber(value?.name)}
            />
            <FormGroup sx={{ display: "inline-flex", ml: 2, mt: 1 }} row>
              <FormControlLabel
                label="Vehicles"
                control={<Checkbox id="vehicles" checked={layerVisibility.vehicles} onChange={handleLayerToggle} />}
              />
              <FormControlLabel
                label="Hubs"
                control={<Checkbox id="hubs" checked={layerVisibility.hubs} onChange={handleLayerToggle} />}
              />
              <FormControlLabel
                label="Satellite View"
                control={<Checkbox id="satellite" checked={layerVisibility.satellite} onChange={handleLayerToggle} />}
              />
            </FormGroup>
          </MDBox>
          <Map
            key={mapKey}
            {...mapCamera}
            onMove={(e) => setMapCamera(e.viewState)}
            onLoad={onLoad}
            projection={projection}
            // styleDiffing off: react-map-gl defaults to diffing one style into the next, and
            // the streets -> satellite-streets hop cannot be diffed -- mapbox logs "Unable to
            // perform style diff: Unimplemented: setSprite. Rebuilding the style from scratch."
            // and the rebuild leaves the map with a style but no live sources, so it stops
            // requesting tiles and paints the style's background colour. That background is
            // near-black, which is the black satellite map users report. Reload cleanly instead.
            styleDiffing={false}
            mapStyle={mapStyle}
            onStyleData={handleStyleChange}
            style={{ width: "75vw", height: "71vh" }}
            mapboxAccessToken={configProvider.MapboxApiKey}
            interactiveLayerIds={[clusterLayer.id, "unclustered-point", hubLayer.id, "hubs-point"]}
            onClick={onClick}
            onMouseMove={onHover}
            reuseMaps={reuseMaps}
            failIfMajorPerformanceCaveat={false}
            onError={(e: any) => {
              if (/webgl/i.test(e?.error?.message ?? "")) setMapError(true);
            }}
            ref={mapRef}
          >
            <Source id="vehicles" type="geojson" data={locations} cluster={true} clusterRadius={50}>
              <Layer {...clusterLayer} />
              <Layer {...unclusteredPointLayer} />
            </Source>
            <Source id="hubs" type="geojson" data={hubSource}>
              <Layer {...hubLayer} />
            </Source>
            {hoverInfo && hoverInfo.feature?.layer?.id == "hubs-layer" && (
              <Popup
                key={hoverInfo.y + hoverInfo.x}
                longitude={hoverInfo.y}
                latitude={hoverInfo.x}
                closeOnMove={true}
                closeButton={true}
                focusAfterOpen={false}
                offset={10}
              >
                <div className="row">
                  <Link to={`/hubs/${hoverInfo.feature.properties.id}`}>{hoverInfo.feature.properties.name}</Link>
                </div>
              </Popup>
            )}{" "}
            {hoverInfo && hoverInfo.feature?.layer?.id == "unclustered-point" && (
              <Popup
                key={hoverInfo.y + hoverInfo.x}
                longitude={hoverInfo.y}
                latitude={hoverInfo.x}
                closeOnMove={true}
                closeButton={true}
                focusAfterOpen={false}
                offset={10}
              >
                <div className="row">
                  Unit #:{" "}
                  <Link
                    to={`/maps/history?unitNumber=${hoverInfo.feature.properties.unitNumber}${parseDatesForLink(
                      hoverInfo.feature.properties.telemetry
                    )}`}
                  >
                    {hoverInfo.feature.properties.unitNumber}
                  </Link>
                </div>
                <div className="row">
                  Heading: {parseProperty(hoverInfo.feature.properties.telemetry)?.heading ?? "N/A"}
                </div>
                <div className="row">
                  Speed: {parseProperty(hoverInfo.feature.properties.telemetry)?.speedMPH ?? "N/A"}/MPH
                </div>
                <div className="row">
                  Lat: {parseProperty(hoverInfo.feature.properties.telemetry)?.latitude ?? "N/A"}
                </div>
                <div className="row">
                  Long: {parseProperty(hoverInfo.feature.properties.telemetry)?.longitude ?? "N/A"}
                </div>
              </Popup>
            )}
          </Map>
        </div>
      </>
    );
  };

  if (loading) return skeleton();
  // The hook rebuilds a lost WebGL context on its own; this is only reached once it has
  // tried and given up, so it offers the retry rather than pretending the map is fine.
  if (mapUnavailable) {
    return (
      <MDBox sx={{ p: 3, textAlign: "center" }}>
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={retry}>
              Try again
            </Button>
          }
        >
          {mapUnavailableMessage}
        </Alert>
      </MDBox>
    );
  }
  if (mapError) {
    return (
      <MDBox sx={{ p: 3, textAlign: "center", color: "error.main" }}>{webglUnavailableMessage}</MDBox>
    );
  }
  return mapView();
};
export default CurrentLocations;
